import { useNavigate } from 'react-router-dom'
import React, { useRef } from 'react'

import { FiTrash } from 'react-icons/fi'
import Bt from '../components/Bt'
import { Context } from '../store/Store'
import { Oval } from 'react-loader-spinner'
import Subtitle from '../components/Subtitle'
import Title from '../components/Title'
import axios from 'axios'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import Select, { components } from 'react-select'

const customOption = (props) => {
  return (
    <components.Option {...props}>
      <div className="flex gap-4">
        {props.data.th && (
          <img
            src={props.data.th}
            style={{ width: 24, height: 24, marginRight: 10, objectFit: 'cover' }}
            alt=""
          />
        )}
        {props.label}
      </div>
    </components.Option>
  )
}

export function StockForm() {
  let history = useNavigate()

  const { app } = React.useContext(Context)
  const selectRef = useRef()
  const [loadingSubmit, setloadingSubmit] = React.useState(false)
  const [, setError] = React.useState(null)
  const [legumes, setLegumes] = React.useState([])
  const [, setUnites] = React.useState([])
  const [, setApiError] = React.useState(false)
  const [existingStockLegumes, setExistingStockLegumes] = React.useState([])

  const { register, handleSubmit, control } = useForm({
    defaultValues: {
      stockItems: [{ legume: '', quantite: '', unite: '' }]
    }
  })

  const { fields, remove } = useFieldArray({
    control,
    name: "stockItems"
  })

  // Récupérer les légumes déjà présents dans le stock avec leur quantité
  React.useEffect(() => {
    // Charger l'état consolidé des stocks pour exclure les légumes déjà déclarés (même à quantité zéro)
    axios
      .get(
        `${
          process.env.REACT_APP_APOLLO_URL || 'https://extranet.biobreizh.org'
        }/wp-json/bb/v1/stocks/${app.profil && app.profil.id ? app.profil.id : '44624'}`
      )
      .then(function (response) {
        if (response.status === 200) {
          // Traiter la nouvelle structure de données
          if (response.data && response.data.stocks && Array.isArray(response.data.stocks)) {
            // Dictionnaire pour stocker la déclaration la plus récente pour chaque légume
            const latestStockByLegume = {};
            const latestDateByLegume = {};

            // Parcourir tous les stocks (déclarations)
            response.data.stocks.forEach(stock => {
              if (stock.items && Array.isArray(stock.items)) {
                // Convertir la date au format DD/MM/YYYY en objet Date
                const dateParts = stock.date.split('/');
                
                // Standardiser l'interprétation des dates
                // Nous considérons toujours le format comme étant DD/MM/YYYY pour éviter les ambiguïtés
                // car c'est le format français standard utilisé dans l'application
                const day = parseInt(dateParts[0]);
                const month = parseInt(dateParts[1]) - 1; // Les mois commencent à 0 en JavaScript
                const year = parseInt(dateParts[2]);
                
                // Créer la date en format DD/MM/YYYY
                const stockDate = new Date(year, month, day);

                // Parcourir tous les items de chaque stock
                stock.items.forEach(item => {
                  const legumeId = item.legume_id.ID;

                  // Vérifier si c'est la déclaration la plus récente pour ce légume
                  if (!latestDateByLegume[legumeId] || stockDate > latestDateByLegume[legumeId]) {
                    latestDateByLegume[legumeId] = stockDate;
                    latestStockByLegume[legumeId] = {
                      legume_id: legumeId,
                      quantite: parseFloat(item.quantite)
                    };
                  }
                });
              }
            });

            // Récupérer tous les IDs des légumes déjà déclarés (même à zéro)
            // Car maintenant on garde tous les légumes dans la liste, même à quantité zéro
            const legumeIdsAlreadyDeclared = [];
            Object.values(latestStockByLegume).forEach(item => {
              legumeIdsAlreadyDeclared.push(item.legume_id);
            });

            setExistingStockLegumes(legumeIdsAlreadyDeclared);
          }
        }
      })
      .catch(function (error) {
        console.log(error);
        // En cas d'erreur, ne pas filtrer les légumes
        setExistingStockLegumes([]);
      });
  }, [app.profil]);

  React.useEffect(() => {
    // Utiliser les légumes du state global
    const legumesData = Array.isArray(app.legumes) ? app.legumes : []
    setLegumes(legumesData)

    // Extraire les unités uniques
    const unitesSet = new Set()
    legumesData.forEach(legume => {
      if (legume && legume.unite) {
        unitesSet.add(legume.unite)
      }
    })
    const unitesStandard = ['kg', 'tonne', 'quintal', 'palox', 'caisse', 'sac', 'botte', 'pièce']
    setUnites([...Array.from(unitesSet), ...unitesStandard.filter(u => !unitesSet.has(u))])

    // Si aucun légume n'est disponible dans le state global, définir des données par défaut
    if (legumesData.length === 0) {
      setApiError(true)
      setLegumes([
        { id: 'default1', title: 'Carotte' },
        { id: 'default2', title: 'Pomme de terre' },
        { id: 'default3', title: 'Tomate' }
      ])
      setUnites(['kg', 'tonne', 'quintal', 'palox', 'caisse', 'sac', 'botte', 'pièce'])
    } else {
      setApiError(false)
    }
  }, [app.legumes])

  const onSubmit = (data) => {
    // Préparation des données pour l'envoi
    // TODO REFACTORING: L'unité envoyée ici est redondante car elle vient déjà du légume
    // Le backend devrait récupérer l'unité directement depuis get_field('unite', $legume_id)
    const stockData = {
      producteurId: app.profil ? app.profil.id : 'default',
      date: new Date().toLocaleDateString('fr-FR'),
      stockItems: data.stockItems.map(item => ({
        legumeId: item.legume?.value || item.legume,
        quantite: item.quantite,
        unite: item.unite?.value || item.unite  // TODO REFACTORING: Cette unité devrait être supprimée du payload
      }))
    }

    setloadingSubmit(true)

    axios
      .post(
        `${
          process.env.REACT_APP_APOLLO_URL || 'https://extranet.biobreizh.org'
        }/wp-json/bb/v1/stock`,
        stockData
      )
      .then(function (response) {
        if (response.status === 200) {
          console.log('Stock créé et Google Sheet mise à jour:', response.data)
          setloadingSubmit(false)
          history('/stocks/status?success=create', { replace: true })
        } else {
          setloadingSubmit(false)
          setError('Erreur de connexion, veuillez vérifier vos identifiants')
        }
      })
      .catch(function (error) {
        console.log(error)
        setloadingSubmit(false)
        setError('Erreur de connexion au serveur. Veuillez réessayer plus tard.')
      })
  }

  return (
    <div className="max-w-7xl mx-auto px-6">
      <div className="w-full py-5 border-gray-200">
        <Subtitle title="Module Stock" />
        <Title title="Formulaire de déclaration" />

        {/* {apiError && (
          <div className="rounded-md bg-price-light/20 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-price" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-price-dark">Mode démo activé</h3>
                <div className="mt-2 text-sm text-price">
                  <p>Le serveur API n'est pas accessible. Les données affichées sont fictives et les soumissions ne seront pas enregistrées.</p>
                </div>
              </div>
            </div>
          </div>
        )} */}

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="border-gray-200 pt-5">
            <div className="w-full">
              <label
                htmlFor="date"
                className="block text-sm font-medium text-gray-700"
              >
                Date
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  name="date"
                  id="date"
                  readOnly
                  className="max-w-lg block w-full shadow-sm focus:ring-price focus:border-price border-gray-300 rounded-md bg-gray-100"
                  value={new Date().toLocaleDateString('fr-FR')}
                />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-5 mt-5">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Déclaration de stock</h3>
            <p className="mt-1 text-sm text-gray-500">
              Ajoutez les légumes et leurs quantités en stock
            </p>
          </div>

          {fields.map((field, index) => (
            <div key={field.id} className="flex flex-wrap items-end space-x-4 mt-4">
              <div className="w-full md:w-1/3">
                <Controller
                  control={control}
                  name={`stockItems.${index}.legume`}
                  rules={{
                    required: 'Veuillez sélectionner un légume',
                  }}
                  render={({ field }) => (
                    <>
                      <label
                        htmlFor={`stockItems.${index}.legume`}
                        className="block text-sm font-medium text-gray-700"
                      >
                        Légume<span className="text-red-500">*</span>
                      </label>
                      <Select
                        {...field}
                        options={legumes
                          .filter(legume => !existingStockLegumes.includes(legume.id))
                          .map((legume) => {
                            return {
                              value: legume.id,
                              label: legume.title,
                              th: legume.thumb,
                              unite: legume.unite
                            }
                          })}
                        inputRef={selectRef}
                        className="max-w-lg block w-full shadow-sm focus:ring-base focus:border-base"
                        classNamePrefix="select"
                        placeholder="Sélectionnez un légume"
                        components={{
                          Option: customOption,
                        }}
                        styles={{
                          control: (provided) => ({
                            ...provided,
                            borderColor: '#D1D5DB',
                            padding: '2px',
                            borderRadius: '0.375rem',
                            '&:hover': {
                              borderColor: '#0F5F3F'
                            }
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected ? '#0F5F3F' : state.isFocused ? '#E5E7EB' : null,
                            color: state.isSelected ? 'white' : '#111827'
                          })
                        }}
                        isClearable
                        onChange={(selectedOption) => {
                          // Mettre à jour le champ légume
                          field.onChange(selectedOption);

                          // TODO REFACTORING: Cette logique est correcte - l'unité vient du légume
                          // Mais idéalement le backend ne devrait pas avoir besoin de l'unité du frontend
                          // Mettre à jour le champ unité si un légume est sélectionné
                          if (selectedOption) {
                            const selectedLegume = legumes.find(l => l.id === selectedOption.value);
                            if (selectedLegume && selectedLegume.unite) {
                              // Mettre à jour le champ unité avec l'unité du légume sélectionné
                              const uniteField = document.querySelector(`input[name="stockItems.${index}.unite"]`);
                              if (uniteField) {
                                uniteField.value = selectedLegume.unite;
                              }

                              // Mettre à jour l'affichage de l'unité
                              const uniteDisplay = document.getElementById(`unite-display-${index}`);
                              if (uniteDisplay) {
                                uniteDisplay.textContent = selectedLegume.unite || 'unité';
                              }
                            }
                          } else {
                            // Si aucun légume n'est sélectionné, réinitialiser l'unité
                            const uniteField = document.querySelector(`input[name="stockItems.${index}.unite"]`);
                            if (uniteField) {
                              uniteField.value = 'unité';
                            }

                            // Réinitialiser l'affichage de l'unité
                            const uniteDisplay = document.getElementById(`unite-display-${index}`);
                            if (uniteDisplay) {
                              uniteDisplay.textContent = 'unité';
                            }
                          }
                        }}
                      />
                    </>
                  )}
                />
              </div>

              <div className="w-full md:w-1/2">
                <label
                  htmlFor={`stockItems.${index}.quantite`}
                  className="block text-sm font-medium text-gray-700"
                >
                  Quantité<span className="text-red-500">*</span>
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    step="0.01"
                    {...register(`stockItems.${index}.quantite`, { required: true })}
                    className="max-w-lg block w-full shadow-sm focus:ring-base focus:border-base border-gray-300 rounded-l-md"
                  />
                  <div className="border-[1px] border-gray-300 rounded-r-md bg-gray-300 p-2">
                    <span id={`unite-display-${index}`}>
                      {fields[index].legume && typeof fields[index].legume === 'object' && fields[index].legume.value ? (
                        legumes.find(l => l.id === fields[index].legume.value)?.unite || 'unité'
                      ) : (
                        'unité'
                      )}
                    </span>
                  </div>
                </div>
                <input
                  type="hidden"
                  name={`stockItems.${index}.unite`}
                  {...register(`stockItems.${index}.unite`)}
                  value={fields[index].legume && typeof fields[index].legume === 'object' && fields[index].legume.value ?
                    legumes.find(l => l.id === fields[index].legume.value)?.unite || 'unité' : 'unité'}
                />
              </div>

              <div className="flex items-center mb-2">
                {index > 0 && (
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="ml-2 p-2 text-red-500 hover:text-red-700"
                  >
                    <FiTrash />
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* <div className="mt-4">
            <button
              type="button"
              onClick={() => append({ legume: '', quantite: '', unite: '' })}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-price hover:bg-price-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-price"
            >
              <FiPlus className="mr-2" /> Ajouter un légume
            </button>
          </div> */}

          <div className="mt-6 border-t border-gray-200 pt-5">
            <div className="flex justify-start">
              {!loadingSubmit ? (
                <Bt title="Envoyer" type="submit" />
              ) : (
                <div className="flex items-center">
                  <Oval color="#0F5F3F" height={40} width={40} />
                  <span className="ml-2">Patienter...</span>
                </div>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default StockForm
